<template>
  <Modal :show-modal="$store?.modals.commentModal || false" @cancel="closeModal">
    <template v-slot:header>
      <p class="text-xl font-bold text-main-color">
        {{ isReply ? $t('page.answerComment') : $t('page.writeComment') }}
      </p>
    </template>
    <template v-slot:content>
      <div class="flex flex-col">
        <Form @submit="postComment" :validation-schema="commentSchema()">
          <div class="mt-5 flex flex-col gap-2">
            <label for="content" class="text-start font-semibold text-main-color">{{
              isReply ? $t('page.answerComment') : $t('page.writeComment')
            }}</label>
            <Field
              v-model="formComment.text"
              as="textarea"
              name="content"
              id="content"
              cols="20"
              rows="3"
              class="w-full rounded border px-8 py-5 placeholder-[#5F5F5F] resize-none max-md:py-4"
            >
            </Field>
            <ErrorMessage name="content" class="text-red-500"></ErrorMessage>
          </div>
          <div class="my-4">
            <input
              ref="file"
              name="files"
              class="hidden"
              type="file"
              multiple
              id="fileUpload"
              @change="onFileChange"
              accept=".jpg,.jpeg,.png"
            />
            <InlineLoader class="!w-8 !h-8" :showLoader="loading"></InlineLoader>
            <label
              v-if="!loading"
              class="flex gap-3 items-center font-bold text-accent-color rounded-md cursor-pointer"
              for="fileUpload"
            >
              <IconClip></IconClip> {{ $t('page.uploadImage') }}
            </label>
          </div>

          <div class="flex flex-col gap-3 mt-4" v-if="files?.length">
            <div
              v-for="file in files"
              :key="file?.detail?.name"
              class="w-full flex items-center p-2 gap-4 border-t border-b"
            >
              <img :src="file.path" alt="uploaded-image" class="h-10 w-10 object-fit" />
              <div class="flex items-center gap-2">
                <label :for="file.detail.name">
                  {{ file.detail.name }}
                </label>
              </div>
              <button
                class="ml-auto mt-1 bg-[#EB5757] p-2.5 rounded"
                type="button"
                @click="remove(files.indexOf(file))"
                title="Remove file"
              >
                <IconTrash></IconTrash>
              </button>
            </div>
          </div>
          <div class="flex flex-col my-6">
            <div class="flex gap-2">
              <Field type="checkbox" class="w-6 h-6" name="terms" id="terms" value="off"></Field>
              <label class="text-start" for="terms">
                <router-link
                  class="text-accent-color hover:text-main-color"
                  :to="{ name: 'comment' }"
                >
                  {{ $t('page.commentTerms') }}
                </router-link>
              </label>
            </div>
            <ErrorMessage name="terms" class="text-red-500"></ErrorMessage>
          </div>
          <div class="mt-5 flex gap-5">
            <SubmitBtn type="submit" :loading="loading">{{ $t('action.send') }}</SubmitBtn>
            <button @click="closeModal" class="w-full text-main-color font-bold">
              {{ $t('action.cancel') }}
            </button>
          </div>
        </Form>
      </div>
    </template>
  </Modal>
</template>

<script>
import api from '@/api';
import { i18n } from '@/i18n';
import { commentSchema } from '@/schemas';
import { Field, Form, ErrorMessage } from 'vee-validate';
import Modal from '@cmp/Modal.vue';
import IconTrash from '@icons/IconTrash.vue';
import IconClip from '@icons/IconClip.vue';
import InlineLoader from '@cmp/InlineLoader.vue';

export default {
  components: {
    Modal,
    Field,
    Form,
    ErrorMessage,
    IconTrash,
    IconClip,
    InlineLoader,
  },
  props: {
    itemId: null,
    origin: 'product',
    isReply: {
      type: Boolean,
      default: false,
    },
    objectId: null,
  },
  data() {
    return {
      loading: false,
      formComment: {
        text: '',
        attachments: [],
      },
      files: [],
      fileUrls: [],
      fileIds: [],
      commentSchema: commentSchema,
      commentOriginUrls: {
        product: 'mall/products/',
        mall: 'mall/products/',
        mall_product: 'mall/products/',

        exhibition: 'exhibition/products/',
        exhibition_product: 'exhibition/products/',

        publication: 'publications/publications/',
        publications: 'publications/publications/',
        publications_publication: 'publications/publications/',
      },
    };
  },
  methods: {
    closeModal() {
      this.$store.modals.commentModal = false;
    },
    remove(i) {
      this.files.splice(i, 1);
      this.fileIds.splice(i, 1);
    },
    async onFileChange() {
      for (var x = 0; x < this.$refs.file.files.length; x++) {
        const fileForm = new FormData();
        const f = this.$refs.file.files[x];
        fileForm.append('path', f);
        this.loading = true;
        const response = await api.post('files/images/', fileForm, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (response && response.status === 201) {
          this.files.push({
            detail: this.$refs.file.files[x],
            path: URL.createObjectURL(this.$refs.file.files[x]),
            isUploaded: true,
          });
          this.fileIds.push(response.data.id);
        } else {
          this.$toast.warning(`${i18n.global.t('warning.UPLOAD_FAILED')}`);
        }
        this.loading = false;
      }
    },
    async postComment() {
      this.loading = true;
      try {
        const body = {
          text: this.formComment.text,
          attachments: this.fileIds,
        };

        let url = '';

        if (this.isReply) {
          url = `comments/${this.objectId}/reply/`;
        } else {
          const originUrl = this.commentOriginUrls[this.origin];

          if (!originUrl) {
            console.error('[COMMENT] Unknown origin:', this.origin);
            this.$toast.error('Unknown comment origin');
            this.loading = false;
            return;
          }

          url = `${originUrl}${this.itemId}/comment/`;
        }

        await api.post(url, body);

        this.$toast.info(`${i18n.global.t('warning.COMMENT_POSTED')}`);
        this.$emit('commentPosted', true);
        this.closeModal();
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
  },
};
</script>
